import { useState, useEffect } from 'react'

export interface PricingEntry {
  price: string
  comparePrice: string
  currency: string
  locale: string
}

export interface PricingInfo extends PricingEntry {
  countryCode: string
  loading: boolean
}

// Easy to extend: add a new country code + pricing entry
export const PRICING_MAP: Record<string, PricingEntry> = {
  CL: {
    price: '$15.990 CLP',
    comparePrice: '$43.999 CLP',
    currency: 'CLP',
    locale: 'es-CL',
  },
  AR: {
    price: '$23.999 ARS',
    comparePrice: '$70.999 ARS',
    currency: 'ARS',
    locale: 'es-AR',
  },
  CO: {
    price: '$63.999 COP',
    comparePrice: '$189.999 COP',
    currency: 'COP',
    locale: 'es-CO',
  },
  MX: {
    price: '$299 MXN',
    comparePrice: '$899 MXN',
    currency: 'MXN',
    locale: 'es-MX',
  },
  UY: {
    price: '$670 UYU',
    comparePrice: '$1.999 UYU',
    currency: 'UYU',
    locale: 'es-UY',
  },
  ES: {
    price: '14,99\u00a0€',
    comparePrice: '42,99\u00a0€',
    currency: 'EUR',
    locale: 'es-ES',
  },
  US: {
    price: 'USD\u00a016,99',
    comparePrice: 'USD\u00a049,99',
    currency: 'USD',
    locale: 'en-US',
  },
}

export const DEFAULT_PRICING: PricingEntry = {
  price: 'USD\u00a016,99',
  comparePrice: 'USD\u00a049,99',
  currency: 'USD',
  locale: 'en-US',
}

async function detectCountry(): Promise<string> {
  const apis = [
    // 1. cloudflare trace (ultra-reliable, no rate limit)
    async () => {
      const res = await fetch('https://cloudflare.com/cdn-cgi/trace', {
        signal: AbortSignal.timeout(3000),
      })
      if (!res.ok) return ''
      const text = await res.text()
      const match = text.match(/^loc=([A-Z]{2})$/m)
      return match?.[1] ?? ''
    },
    // 2. ipwho.is (free, HTTPS, generous limits)
    async () => {
      const res = await fetch('https://ipwho.is/', {
        signal: AbortSignal.timeout(3000),
      })
      if (!res.ok) return ''
      const data = await res.json()
      return data?.country_code?.trim() ?? ''
    },
    // 3. ipapi.co JSON (fallback)
    async () => {
      const res = await fetch('https://ipapi.co/json/', {
        signal: AbortSignal.timeout(3000),
      })
      if (!res.ok) return ''
      const data = await res.json()
      return data?.country_code?.trim() ?? ''
    },
    // 4. freeipapi.com (no key, HTTPS)
    async () => {
      const res = await fetch('https://freeipapi.com/api/json', {
        signal: AbortSignal.timeout(3000),
      })
      if (!res.ok) return ''
      const data = await res.json()
      return data?.countryCode?.trim() ?? ''
    },
  ]

  for (const api of apis) {
    try {
      const code = await api()
      if (code && code.length === 2) return code
    } catch {
      // try next
    }
  }

  return ''
}

export function usePricing(): PricingInfo {
  // Start as null to avoid flicker — render nothing until country is known
  const [result, setResult] = useState<PricingInfo | null>(null)

  useEffect(() => {
    detectCountry().then((countryCode) => {
      const entry = PRICING_MAP[countryCode] ?? DEFAULT_PRICING
      setResult({ ...entry, countryCode, loading: false })
    })
  }, [])

  // While loading, return default pricing with loading: true
  // Using default values prevents layout shift — prices are invisible until resolved
  if (!result) {
    return { ...DEFAULT_PRICING, countryCode: '', loading: true }
  }

  return result
}
